<template>
	<!-- Drawer to see lessonplans from library -->
	<LessonPlanLibraryDrawer
		v-if="showLibrary"
		ref="libraryDrawer"
		@requestClose="hideLibrary"
		@selectLessonPlan="handleSelectLessonPlanFromLibrary"
		@createVariant="handleCreateVariant"
	/>

	<!-- Drawer to create or edit a lessonplan -->
	<LessonPlanEditor
		v-if="showEditor"
		:lessonPlan="editingLessonPlan"
		:context="'agenda'"
		:skipOpenAnimation="skipEditorAnimation"
		:suggestedGoals="suggestedGoals"
		@requestClose="closeEditor"
		@cancel="handleEditorCancel"
		@saved="handleEditorSaved"
	/>

	<!-- Drawer to view and edit selected lessonplans -->
	<LessonPlanViewer
		v-if="viewingLessonPlan"
		:withEditButton="viewingLessonPlan.canBeEditedBy(usersStore.me)"
		:variantButtonLabel="variantButtonLabel(viewingLessonPlan)"
		:withLibraryButton="canUseLibraryButton(viewingLessonPlan)"
		:lessonPlan="viewingLessonPlan"
		:skipOpenAnimation="skipViewerAnimation"
		@requestUnmount="hideViewer"
		@editLessonPlan="handleEditFromViewer"
		@createVariant="handleCreateVariant"
	/>

	<!-- Drawer to show iconlibrary to choose icons -->
	<IconSelector
		v-if="showIconSelector"
		:isIconSelectableCallback="() => true"
		:group="'activity'"
		:model="selectedItem"
		:classroom="classroomsStore.currentClassroom"
		@icon:selected="setIcon($event)"
		@request-close="closeIconSelector"
	/>

	<!-- Drawer to edit lesson item settings -->
	<LessonItemPupilSelection
		v-if="showLessonItemSettings && selectedLessonItemForEditing"
		:key="selectedLessonItemForEditing.id || 'pending'"
		:lessonItem="selectedLessonItemForEditing"
		@request-close="hideForm"
		@update:model-value="afterLessonItemChanged"
	/>

	<!-- Activity multi-select for activities -->
	<div class="flex justify-center print:hidden">
		<div
			v-show="isBoardTypeActivities"
			class="mb-4 border-4 border-primary rounded-lg inline-block w-full sm:max-w-xl"
		>
			<MultiSelect
				:values="activityFilterValues"
				:selectedIds="selectedActivityIds"
				class="w-full"
				buttonFieldClasses="input-bordered"
				inputFieldClasses="input-bordered"
				@itemSelected="addLessonItemWithActivity"
				@itemUnselected="removeLessonItemWithActivity"
			>
				{{ $t("Select the activities to offer") }}

				<template #item="itemProps">
					<div class="w-full flex py-2 pl-3 hover:text-accent-content hover:bg-accent">
						<div class="flex-shrink-0 flex items-center justify-center w-16">
							<div class="avatar">
								<div class="w-10 mask mask-squircle">
									<ActivitySymbol :activity="itemProps.value.meta.activity" class="w-10" />
								</div>
							</div>
						</div>
						<div class="flex flex-1 items-center justify-between">
							<div class="flex-1 overflow-hidden pl-4 pr-12 py-2">
								{{ itemProps.value.label }}
								<!-- <p class="opacity-40">{{ itemProps.value.info }}</p> -->
							</div>
							<div class="flex-shrink-0">
								<span
									v-show="itemProps.selected"
									class="text-accent-content absolute inset-y-0 right-0 flex items-center pr-4"
								>
									<CheckIcon class="h-7 w-7"></CheckIcon>
								</span>
							</div>
						</div>
					</div>
				</template>
			</MultiSelect>
		</div>
	</div>

	<!-- If not activities board & empty state -->
	<div
		v-if="!isBoardTypeActivities && lesson.lessonItems.length === 0 && !pendingLessonItem"
		class="flex flex-col items-center justify-center gap-4 lg:pt-24"
	>
		<div>{{ $t("Add a lesson plan") }}...</div>
		<div class="flex gap-4 justify-center items-center w-full">
			<button
				type="button"
				class="btn btn-primary rounded-md"
				@click="handleIntentToAddLessonPlanToGeneralLesson(true)"
			>
				<PlusIcon class="w-5 h-5 mr-1" />
				{{ $t("New") }}
			</button>
			<button
				type="button"
				class="btn btn-primary rounded-md"
				@click="handleIntentToAddLessonPlanToGeneralLesson(false)"
			>
				<PlusIcon class="w-5 h-5 mr-1" />
				{{ $t("Open library") }}
			</button>
		</div>
	</div>

	<!-- Warning for duplicate icons in use -->
	<div v-show="duplicateIconsInUse" class="alert alert-warning">
		<div class="flex gap-2">
			<ExclamationTriangleIcon class="stroke-current flex-shrink-0 h-6 w-6" />
			<span>{{ $t("Warning: one or more icons will be displayed multiple times") }}</span>
		</div>
	</div>

	<!-- If activities board or lesson items exist or pending lesson item -->
	<div
		v-if="isBoardTypeActivities || lesson.lessonItems.length || pendingLessonItem"
		id="activities-collection"
		class="w-full mt-12 pb-8"
	>
		<!-- Empty state -->
		<div v-if="isBoardTypeActivities && !lesson.lessonItems.length" class="self-center col-span-full">
			<div class="flex flex-col text-center gap-6 items-center text-base-content-light italic w-full sm:max-w-xs mx-auto rounded-lg mt-20">
				<svg class="w-20 h-20 opacity-30 block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
					<path class="opacity-50" d="M496 384H176a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h320a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm0-320H176a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h320a16 16 0 0 0 16-16V80a16 16 0 0 0-16-16zm0 160H176a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h320a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16z"/>
					<path d="M80 368H16a16 16 0 0 0-16 16v64a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-64a16 16 0 0 0-16-16zm0-320H16A16 16 0 0 0 0 64v64a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16zm0 160H16a16 16 0 0 0-16 16v64a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-64a16 16 0 0 0-16-16z" />
				</svg>
				{{ $t('Go ahead and add the first activity') }}
			</div>
		</div>
		<div class="overflow-x-auto">
			<TransitionGroup
				name="animatedlist"
				tag="div"
				id="activity-collection"
				class="grid grid-cols-[14.5rem_repeat(7,minmax(4.5rem,1fr))] col-span-full w-full auto-rows-auto"
			>
				<!-- Header -->
				<div v-if="lesson.lessonItems.length || pendingLessonItem" class="grid grid-cols-subgrid col-span-full items-center" :key="'activity-collection-header'">
					<div
						v-if="isBoardTypeActivities"
						class="bg-indigo-200 border border-indigo-400 rounded-md rounded-b-none flex flex-col gap-1 p-2 h-14 text-center"
					>
						<span class="font-semibold text-sm uppercase">
							{{ $t("Activity board") }}
						</span>
						<div class="text-xs text-center">
							<i18n-t keypath="{seatCount} seats for {childCount} children" tag="div">
								<template #seatCount>
									<span class="text-white font-semibold rounded-full px-1.5" v-text="sumActivitySeats" :class="[sumActivitySeats >= requiredSeats? 'bg-green-600' : 'bg-red-600']"></span>
								</template>
								<template #childCount>
									<span class="bg-white text-black font-semibold rounded-full px-1.5" v-text="requiredSeats"></span>
								</template>
							</i18n-t>
						</div>

					</div>
					<div
						class="flex gap-2 items-center font-semibold text-sm uppercase border-b border-base-200 rounded-md rounded-b-none justify-start p-2 px-5 h-14"
						:class="isBoardTypeActivities ? 'col-span-3' : 'col-span-5'"
					>
						<div class="font-semibold uppercase mr-2">{{ $t("Lesson plan") }}</div>
						<div class="flex gap-2" v-if="!isBoardTypeActivities">
							<button
								type="button"
								class="btn btn-sm btn-primary rounded-md"
								@click="handleIntentToAddLessonPlanToGeneralLesson(true)"
							>
								<PlusIcon class="w-4 h-4 mr-1" />
								{{ $t("New") }}
							</button>
							<button
								type="button"
								class="btn btn-sm btn-primary rounded-md"
								@click="handleIntentToAddLessonPlanToGeneralLesson(false)"
							>
								<PlusIcon class="w-4 h-4 mr-1" />
								{{ $t("Open library") }}
							</button>
						</div>
					</div>
					<!-- empty space -->
					<div v-if="isBoardTypeActivities" class="border-b border-base-200 h-full"></div>
					<div
						class="font-semibold uppercase col-span-3 text-sm border-b border-base-200 rounded-md rounded-b-none flex items-center justify-start gap-2 p-2 h-14"
					>
						{{ $t("For") }}...
					</div>
				</div>

				<!-- Lessonplans & activities -->
				<div class="col-span-full grid grid-cols-subgrid auto-rows-auto" :key="'lesson-items-grid'">
					<template v-for="(lessonItem, index) in displayLessonItems" :key="lessonItem.id">
						<div
							v-if="isBoardTypeActivities"
							:class="[
								'grid grid-cols-[2fr_3fr] items-center h-full min-h-24 px-2 bg-indigo-500 border-b border-indigo-400',
								index === displayLessonItems.length - 1 ? 'rounded-b-sm border-none' : '',
							]"
						>
							<div class="flex items-center justify-center">
								<div class="relative group/activity-icon hover:cursor-pointer" @click.stop.prevent="openIconSelector(lessonItem)">
									<div class="avatar bg-white rounded-lg">
										<div class="mask mask-squircle">
											<ActivitySymbol
												:activity="lessonItem"
												v-if="lessonItem"
												class="w-16 h-16"
											/>
										</div>
									</div>
									<button
										class="absolute -top-2 -right-2 btn btn-circle btn-sm z-10 opacity-60 group-hover/activity-icon:opacity-100 group-hover/activity-icon:scale-105 transition-all duration-100"
										style="box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3)"
									>
										<PencilIcon class="h-4 w-4" />
									</button>
								</div>
							</div>

							<div class="flex flex-col gap-2 min-w-0 ml-4">
								<div class="text-white truncate">{{ lessonItem.activity?.name }}</div>
								<NumberInput
									:key="`seats-${lessonItem.id || lessonItem.activity?.id}-${index}`"
									v-model="lessonItem.seats"
									:stepValue="1"
									class="border-indigo-600"
									buttonClasses="text-indigo-800"
									input-classes="bg-indigo-500 text-white font-semibold text-[1rem]"
									size="sm"
								/>
							</div>
						</div>
						<!-- Column 1: activity / title -->
						<div
							class="border-b border-base-100 p-1"
							:class="[
								isBoardTypeActivities ? 'col-span-4' : 'col-span-5',
							]"
							@click="lessonItem.lessonPlan ? showLessonPlanViewer(lessonItem) : null"
						>
							<template v-if="lessonItem.activity && !lessonItem.lessonPlan">
								<div class="flex gap-3 px-4 py-2 h-full items-center">
									<button
										type="button"
										class="btn btn-primary btn-xs"
										@click.stop="handleIntentToAddLessonPlanToActivity(lessonItem, true)"
									>
										<PlusIcon class="w-3 h-3" />
										{{ $t("New") }}
									</button>
									<button
										type="button"
										class="btn btn-primary btn-xs"
										@click.stop="handleIntentToAddLessonPlanToActivity(lessonItem)"
									>
										<PlusIcon class="w-3 h-3" />
										{{ $t("Open library") }}
									</button>
								</div>
							</template>
							<template v-else-if="lessonItem.lessonPlan">
								<div class="flex justify-between items-center h-full py-2 px-4 rounded-md cursor-pointer hover:bg-base-100">
									<div class="w-full">
										<div v-if="lessonItem.lessonPlan" class="flex items-center gap-2.5">
											<h1 class="font-semibold truncate max-w-48">
												<span>{{ lessonItem.lessonPlan.title }}</span>
											</h1>
											<!-- Status only, for any plan in *my* library (inLibrary is per user); adding, removing and
											     collections happen in the viewer's library button -->
											<div
												v-if="lessonItem.lessonPlan.isInMyLibrary"
												class="relative shrink-0 tooltip"
												:data-tip="lessonItem.lessonPlan.isOutdatedVersion() ? $t('In My Library, but this lesson uses an older version') : $t('In My Library')"
												data-testid="in-library-indicator"
											>
												<!-- orange: this lesson uses an older version than the one in my library -->
												<span class="relative flex items-center justify-center w-6 h-6">
													<RectangleStackIcon class="w-4 h-4 text-base-content" />
													<span
														class="w-3 h-3 absolute top-0 -right-0.5 rounded-full flex justify-center items-center"
														:class="lessonItem.lessonPlan.isOutdatedVersion() ? 'bg-orange-500' : 'bg-green-500'"
													>
														<CheckIcon class="w-2.5 h-2.5 text-base" />
													</span>
												</span>
											</div>
										</div>

										<div v-if="showCreator(lessonItem.lessonPlan)" class="flex gap-2 mb-1">
											<div class="flex items-center gap-2 text-xs text-accent-focus bg-accent-focus/10 py-0.5 px-2 mt-0.5 max-w-max rounded">
												<!-- Phosphor icon PenNib -->
												<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="currentColor" viewBox="0 0 256 256">
													<path d="M248,92.68a15.86,15.86,0,0,0-4.69-11.31L174.63,12.68a16,16,0,0,0-22.63,0L123.57,41.11l-58,21.77A16.06,16.06,0,0,0,55.35,75.23L32.11,214.68A8,8,0,0,0,40,224a8.4,8.4,0,0,0,1.32-.11l139.44-23.24a16,16,0,0,0,12.35-10.17l21.77-58L243.31,104A15.87,15.87,0,0,0,248,92.68Zm-69.87,92.19L63.32,204l47.37-47.37a28,28,0,1,0-11.32-11.32L52,192.7,71.13,77.86,126,57.29,198.7,130ZM112,132a12,12,0,1,1,12,12A12,12,0,0,1,112,132Zm96-15.32L139.31,48l24-24L232,92.68Z"></path>
												</svg>
												<p>{{ lessonItem.lessonPlan.creatorName }}</p>
												<div v-if="lessonItem.lessonPlan.coCreatorName" class="flex items-center gap-2">
													<span>/</span><span>{{ lessonItem.lessonPlan.coCreatorName }}</span>
												</div>
											</div>
										</div>

										<div
											class="text-sm text-base-content-light"
											:class="lessonItem.activity ? 'line-clamp-2' : 'line-clamp-4'"
										>
											{{ extractText(lessonItem.lessonPlan.instructions) }}
										</div>
									</div>
									<div class="self-center mx-8 shrink-0" :class="isBoardTypeActivities ? 'mr-4' : ''">
										<ChevronRightIcon class="w-4 h-4 text-base-content-light" />
									</div>
								</div>
							</template>
							<template v-else>
								<Skeleton content-type="userlist" :items="1" :withThumb="false" />
							</template>
						</div>

						<!-- Column 2: lessonplan button -->
						<div class="col-start-6 col-span-2 border-b border-base-100">
							<div class="flex items-center justify-start gap-1.5 p-2 h-full text-sm" @click="showLessonItemPupilSelection(lessonItem)">
								<div class="group/pupils hover:cursor-pointer flex items-center">
									<PupilSelectionSummary :selectedPupils="selectedPupilsFor(lessonItem)" :allPupils="pupilsStore.items" class="min-w-20 max-w-min" />
									<button
										class="btn btn-ghost btn-circle btn-sm opacity-25 group-hover/pupils:opacity-100 group-hover/pupils:scale-105 transition-all duration-200"
									>
										<PencilIcon class="h-4 w-4" />
									</button>
								</div>
							</div>
						</div>

						<!-- Column 3: trash button -->
						<div class="text-center print:hidden flex justify-end items-center col-start-8 p-2 h-full border-b border-base-100">
							<button
								@click="removeLessonItem(lessonItem)"
								class="btn btn-ghost btn-circle btn-sm bg-base-100 hover:bg-danger text-base-content-light hover:text-base ml-2"
							>
								<TrashIcon class="w-4 h-4" />
							</button>
						</div>
					</template>
				</div>
			</TransitionGroup>
		</div>
	</div>
</template>

<style scoped>
#activity-collection tr:last-child td.seats-icon-cell {
	@apply rounded-b-lg;
}
</style>

<script lang="ts">
// models
import { LessonItem } from "@/models/LessonItem.model";
import { Pupil } from "@/models/Pupil.model";
import { Lesson } from "@/models/Lesson.model";
import { Theme } from "@/models/Theme.model";
import { LessonPlan } from "@/models/LessonPlan.model";
// utils
import { tiptapUtils } from '@/utils/TipTap.util.ts';
// icons
import {
	TrashIcon,
	PlusIcon,
	PencilIcon,
	CheckIcon,
	ChevronRightIcon,
	RectangleStackIcon,
	ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";
import { UsersIcon } from "@heroicons/vue/24/solid";
// stores
import { mapState, mapStores } from "pinia";
import { useUsersStore } from "@/stores/Users.store";
import { useActivitiesStore } from "@/stores/Activities.store";
import { usePupilsStore } from "@/stores/Pupils.store";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useThemesStore } from "@/stores/Themes.store";
import { FilterParamValue } from "@/stores/filters/FilterParamValue";
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
// components
import Skeleton from "@/components/ui/Skeleton.vue";
import MultiSelect from "@/components/ui/MultiSelect.vue";
import ActivitySymbol from "@/components/ui/symbols/ActivitySymbol.vue";
import PupilSelectionSummary from "@/components/PupilSelectionSummary.vue";
import LessonPlanViewer from "@/components/LessonPlanViewer.vue";
import LessonPlanEditor from "@/components/LessonPlanEditor.vue";
import LessonPlanLibraryDrawer from "@/components/LessonPlanLibraryDrawer.vue";
import LessonItemPupilSelection from "@/components/LessonItemPupilSelection.vue";
import NumberInput from "@/components/ui/NumberInput.vue";
import IconSelector from "@/components/IconSelector.vue";

export default {
	props: {
		lesson: {
			type: Lesson,
			required: true,
		},
	},

	components: {
		PupilSelectionSummary,
		Skeleton,
		MultiSelect,
		PencilIcon,
		PlusIcon,
		TrashIcon,
		CheckIcon,
		UsersIcon,
		ChevronRightIcon,
		RectangleStackIcon,
		ExclamationTriangleIcon,
		IconSelector,
		NumberInput,
		ActivitySymbol,
		LessonPlanViewer,
		LessonPlanEditor,
		LessonPlanLibraryDrawer,
		LessonItemPupilSelection,
	},

	data() {
		return {
			showLessonItemSettings: false,
			selectedLessonItemForEditing: null as LessonItem | null,
			duplicateIconsInUse: false,
			editingLessonPlan: null as any,
			viewingLessonPlan: null as any,
			isViewingFromLibraryDrawer: false, // true when viewing from library drawer
			suggestedGoals: [] as any[],
			pendingLessonItem: null as LessonItem | null, // temporary lesson item not yet added to array
			selectedItem: null as LessonItem | null,
			// show drawers
			showLibrary: false,
			showEditor: false,
			skipEditorAnimation: false,
			skipViewerAnimation: false,
			pendingLibraryClose: false,
			pendingEditorPlan: null as any,
			editingFromTemplate: false,
			showIconSelector: false,
		};
	},

	computed: {
		...mapStores(useActivitiesStore, usePupilsStore, useClassroomsStore, useLessonPlansStore, useThemesStore, useDiscoverStateStore, useUsersStore),

		...mapState(useActivitiesStore, ["activities"]),
		...mapState(usePupilsStore, ["pupils"]),

		extractText() {
			return tiptapUtils.extractText;
		},

		requiredSeats() {
			return this.pupilsStore.items.length;
		},

		sumActivitySeats() {
			if (!this.lesson) {
				return 0;
			}

			if (this.lesson.lessonItems.length) {
				return this.lesson.lessonItems
					.map((x) => x.seats || 0)
					.reduce(function (prev, curr) {
						return prev + curr;
					});
			}
			return 0;
		},

		activityFill() {
			const maxSeats = this.requiredSeats || 1; // avoid division by zero
			const res = Math.min(100, Math.round((this.sumActivitySeats / maxSeats) * 100));
			return res;
		},

		activityFilterValues() {
			return this.activities.map(
				(activity) => new FilterParamValue(activity.id, activity.name, { activity: activity })
			);
		},

		selectedActivityIds() {
			return this.lesson.lessonItems.filter((el) => el.activity).map((el) => el.activity.id);
		},

		isBoardTypeActivities() {
			return this.lesson.boardType === Lesson.BOARD_TYPE_ACTIVITIES;
		},

		// Include pending lesson item in the display collection
		displayLessonItems() {
			const items = [...this.lesson.lessonItems];
			// Add pending item to the beginning if it exists
			if (this.pendingLessonItem) {
				items.unshift(this.pendingLessonItem);
			}
			return items;
		},
	},

	mounted() {
		this.initialize();
		this.loadSuggestedGoals();
	},

	unmounted() {
		this.discoverStateStore.setLessonPlanIdsInBlock([]);
	},

	methods: {
		/** null while the lesson item has no pupil restriction loaded yet, so the summary stays empty. */
		selectedPupilsFor(lessonItem: LessonItem): Pupil[] | null {
			if (!lessonItem.allowedPupilIds) {
				return null;
			}
			return this.pupilsStore.items.filter((pupil: Pupil) => lessonItem.isPupilSelected(pupil));
		},

		async initialize() {
			this.activitiesStore.load();
			await this.pupilsStore.loadAllForClassroom();
			this.checkForDuplicateIcons();
		},

		openIconSelector(lessonItem: LessonItem) {
			this.selectedItem = lessonItem;
			this.showIconSelector = true;
		},

		setIcon(icon: any) {
			if (!this.selectedItem) return;
			this.selectedItem.icon = icon;
			this.showIconSelector = false;
			this.selectedItem = null;
			this.checkForDuplicateIcons();
		},

		closeIconSelector() {
			this.showIconSelector = false;
			this.selectedItem = null;
		},

		// 'Proxy' method (for readability)
		async addLessonItemWithActivity(filterValue: FilterParamValue) {
			const activity = this.activitiesStore.activities.find((a) => a.id == filterValue.id);
			await this.addLessonItem(activity);
		},

		async handleIntentToAddLessonPlanToGeneralLesson(skipLibrary: boolean = false) {
			await this.pupilsStore.loadAllForClassroom();

			// Create a pending lesson item without adding it to the array yet
			let newLessonItem = LessonItem.create(null);
			newLessonItem.allowedPupilIds = this.pupilsStore.items.map((p) => p.id);
			this.pendingLessonItem = newLessonItem;
			this.selectedLessonItemForEditing = newLessonItem;

			if (skipLibrary) {
				// Create new lesson plan directly and add to array
				this.editingLessonPlan = await this.lessonPlansStore.new(false);
				this.lesson.addLessonItem(newLessonItem);
				this.pendingLessonItem = null;
				this.showEditor = true;
			} else {
				// Show library drawer
				this.syncLessonPlanIdsInBlock();
				this.showLibrary = true;
			}
		},

		async handleIntentToAddLessonPlanToActivity(lessonItem: LessonItem, skipLibrary: boolean = false) {
			this.selectedLessonItemForEditing = lessonItem;

			if (skipLibrary) {
				// Create new lesson plan directly
				this.editingLessonPlan = await this.lessonPlansStore.new(false);
				this.showEditor = true;
				this.showLibrary = false;
			} else {
				// Show library drawer
				this.syncLessonPlanIdsInBlock();
				this.showLibrary = true;
			}
		},

		syncLessonPlanIdsInBlock() {
			const ids = this.lesson.lessonItems
				.filter((item: LessonItem) => item.lessonPlan?.id)
				.map((item: LessonItem) => item.lessonPlan!.id!);
			this.discoverStateStore.setLessonPlanIdsInBlock(ids);
		},

		loadSuggestedGoals() {
			const themes = this.themesStore.getActiveAt(this.lesson.start);
			this.suggestedGoals = [];
			themes.forEach((theme: Theme) => {
				theme.goals.forEach((goal: any) => {
					if (!this.suggestedGoals.find((g: any) => g.id === goal.id)) {

						// Don't add themes if their name is longer than 80 characters
						if (theme.name.length > 80) {
							return;
						}

						this.suggestedGoals.push(goal);
					}
				});
			});
		},

		hideLibrary() {
			this.showLibrary = false;

			if (this.pendingEditorPlan) {
				// Open editor after library finishes closing
				this.editingLessonPlan = this.pendingEditorPlan;
				this.showEditor = true;
				this.pendingEditorPlan = null;
			} else {
				this.cleanupSelectionAfterClose();
			}
		},

		hideViewer() {
			this.viewingLessonPlan = null;
			this.isViewingFromLibraryDrawer = false;
			this.skipViewerAnimation = false;

			// Chain: after viewer finishes closing, start library close animation
			if (this.pendingLibraryClose) {
				this.pendingLibraryClose = false;
				const libraryDrawer = this.$refs.libraryDrawer as any;
				if (libraryDrawer && libraryDrawer.animatedClose) {
					libraryDrawer.animatedClose();
				} else {
					this.showLibrary = false;
				}
			} else if (this.pendingEditorPlan) {
				// Viewer was opened straight from the lesson item: open the editor once it closed
				this.editingLessonPlan = this.pendingEditorPlan;
				this.pendingEditorPlan = null;
				this.showEditor = true;
			}
		},

		handleEditFromViewer(lessonPlan: any) {
			// Close the viewer, then open the editor with the lesson plan (no animation — instant swap)
			this.viewingLessonPlan = null;
			this.skipEditorAnimation = true;
			this.editingLessonPlan = lessonPlan;
			this.showEditor = true;
		},

		handleCreateVariant(originalLessonPlan: any) {
			if (!this.selectedLessonItemForEditing) {
				return;
			}

			// A new, unsaved plan based on the original, owned and created by the current user
			const clonedPlan = originalLessonPlan.createVariantFor(this.usersStore.me);

			// Don't assign the cloned plan to the lesson item yet, wait for editor save
			this.editingFromTemplate = true;
			this.pendingEditorPlan = clonedPlan;

			// From the library: viewer animates out, hideViewer chains the library close,
			// then hideLibrary opens the editor.
			// Straight from the lesson item: hideViewer opens the editor.
			if (this.showLibrary) {
				this.pendingLibraryClose = true;
			}
		},

		handleEditorCancel(lessonPlan: any) {
			// Swap back to the viewer instantly (no animation)
			this.showEditor = false;
			this.editingLessonPlan = null;
			this.skipEditorAnimation = false;

			// check if we were editing from a template
			if (this.editingFromTemplate) {
				// Cancel from template: don't assign the plan to the lesson item
				this.editingFromTemplate = false;
				this.cleanupSelectionAfterClose();
			} else {
				// Normal cancel: swap back to the viewer instantly (no animation)
				this.skipViewerAnimation = true;
				this.viewingLessonPlan = lessonPlan;
				this.cleanupSelectionAfterClose();
			}
		},

		async closeEditor() {
			this.showEditor = false;
			this.editingLessonPlan = null;
			this.skipEditorAnimation = false;
			this.cleanupSelectionAfterClose();
		},

		handleEditorSaved(savedLessonPlan: any) {
			this.editingFromTemplate = false;

			// Assign the saved lesson plan to the current lesson item
			if (this.selectedLessonItemForEditing) {
				this.selectedLessonItemForEditing.lessonPlan = savedLessonPlan; // this will also replace any existing lesson plan on the lesson item (e.g. in case of "replace with variant")

				// If the lesson item was pending, add it to the lesson
				if (this.pendingLessonItem && this.pendingLessonItem === this.selectedLessonItemForEditing) {
					if (!this.lesson.lessonItems.includes(this.pendingLessonItem)) {
						this.lesson.addLessonItem(this.pendingLessonItem);
					}
					this.pendingLessonItem = null;
				}

				this.selectedLessonItemForEditing = null;
			}
		},

		handleSelectLessonPlanFromLibrary(lessonPlan: any) {
			// Assign the lesson plan to the lesson item
			if (this.selectedLessonItemForEditing) {
				this.selectedLessonItemForEditing.lessonPlan = lessonPlan;
				// Add pending item to array if it was pending
				if (this.pendingLessonItem && this.pendingLessonItem === this.selectedLessonItemForEditing) {
					this.lesson.addLessonItem(this.pendingLessonItem);
					this.pendingLessonItem = null;
				}
			}

			this.selectedLessonItemForEditing = null;
			this.checkForDuplicateIcons();
		},

		/**
		 * Select an activity
		 * @param activity
		 */
		async addLessonItem(activity = null) {
			await this.pupilsStore.loadAllForClassroom();

			let newLessonItem = LessonItem.create(activity);
			newLessonItem.allowedPupilIds = this.pupilsStore.items.map((p) => p.id);

			// Ensure seats is properly initialized with its own value (not a reference)
			if (activity && activity.seats !== undefined && activity.seats !== null) {
				// Create an independent copy of the seats value
				newLessonItem.seats = Number(activity.seats);
			} else {
				newLessonItem.seats = 2; // Default seats value
			}

			this.lesson.addLessonItem(newLessonItem);

			if (this.isBoardTypeActivities) {
				this.checkForDuplicateIcons();
			}
			return newLessonItem;
		},

		// Remove the lesson item that has the given activity
		removeLessonItemWithActivity(filterValue: FilterParamValue) {
			let lessonItem = this.lesson.lessonItems.find((l) => l.activity && l.activity.id == filterValue.id);
			if (lessonItem) {
				this.removeLessonItem(lessonItem);
			}
		},
		// Remove the lesson item with the given index
		removeLessonItem(lessonItem: LessonItem) {
			this.lesson.removeLessonItem(lessonItem);
			this.checkForDuplicateIcons();
		},

		hideForm() {
			// Close any open drawers
			this.showLibrary = false;
			this.showEditor = false;
			this.viewingLessonPlan = null;
			this.editingLessonPlan = null;
			this.showLessonItemSettings = false;
			this.cleanupSelectionAfterClose();
		},

		cleanupSelectionAfterClose() {
			// Clean up pending lesson item if no lesson plan was assigned
			if (this.pendingLessonItem && !this.pendingLessonItem.lessonPlan) {
				this.pendingLessonItem = null;
			} else if (this.selectedLessonItemForEditing && !this.selectedLessonItemForEditing.lessonPlan) {
				if (this.lesson.boardType !== Lesson.BOARD_TYPE_ACTIVITIES) {
					this.lesson.removeLessonItem(this.selectedLessonItemForEditing);
				}
			} else if (
				this.selectedLessonItemForEditing &&
				this.selectedLessonItemForEditing.lessonPlan &&
				this.selectedLessonItemForEditing.lessonPlan.isValid() === false
			) {
				// For choice activity where an invalid plan was attached, clear it
				this.selectedLessonItemForEditing.lessonPlan = null;
			}

			this.selectedLessonItemForEditing = null;
			this.showLessonItemSettings = false;
			this.checkForDuplicateIcons();
		},

		showLessonPlanViewer(lessonItem: LessonItem) {
			this.selectedLessonItemForEditing = lessonItem;

			if (lessonItem.lessonPlan) {
				// View existing lesson plan
				this.viewingLessonPlan = lessonItem.lessonPlan;
				this.isViewingFromLibraryDrawer = false;
			} else {
				// No lesson plan, this shouldn't happen with new flow
				// but handle it just in case
				this.showLibrary = true;
			}
		},

		showLessonItemPupilSelection(lessonItem: LessonItem) {
			this.selectedLessonItemForEditing = lessonItem;
			this.showLessonItemSettings = true;
		},

		afterLessonItemChanged(updatedItem: LessonItem) {
			if (this.selectedLessonItemForEditing) {
				this.selectedLessonItemForEditing.setFromModel(updatedItem);
			}

			this.checkForDuplicateIcons();
		},

		checkForDuplicateIcons() {
			let iconList = [];
			for (const lessonItem of this.lesson.lessonItems) {
				let icon = lessonItem.icon;
				if (icon) {
					for (let i = 0; i < iconList.length; i++) {
						if (iconList[i].equals(icon)) {
							this.duplicateIconsInUse = true;
							return;
						}
					}
					iconList.push(icon);
				}
			}
			this.duplicateIconsInUse = false;
		},

		// A variant replaces the plan on the lesson item: of someone else's plan, it's your own variant.
		variantButtonLabel(lessonPlan: LessonPlan): string {
			const me = this.usersStore.me;
			return me && lessonPlan.isCreatedBy(me)
				? this.$t("Replace with variant")
				: this.$t("Replace with own variant");
		},

		// The viewer's library button (add to or remove from My Library, collections), as the API allows it
		canUseLibraryButton(lessonPlan: LessonPlan) {
			const me = this.usersStore.me;
			// Not for an unsaved lesson plan
			if (!lessonPlan.id || !me) return false;
			// Not for your own plan once you removed it from your library (it stays frozen on its lessons)
			if (!lessonPlan.isInMyLibrary && lessonPlan.isOwnedBy(me)) return false;

			// OK to use the library button if it's in my library, it is owned by someone else (and not a throw-away), or was created by me
			return lessonPlan.isInMyLibrary || !!lessonPlan.owner || lessonPlan.isCreatedBy(me);
		},

		// similar function exists on LessonPlanViewer component - keep them in sync
		showCreator(lessonPlan: LessonPlan): boolean {
			// show creator info if it's not your own lesson plan...
			if (this.usersStore.me && !lessonPlan.isCreatedBy(this.usersStore.me)) {
				return true;
			// ...or if it's your own but you are not the original creator
			} else if( lessonPlan.originalCreator?.id && lessonPlan.originalCreator.id !== this.usersStore.me?.id) {
				return true;
			}
			return false;
		}
	},
};
</script>
